import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState, JSX } from 'react';

import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { LeaderboardActions, LeaderboardMultiplier } from '@/types/leaderboard.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import Modal from '@ui/modal/modal';
import Switch from '@ui/switch/switch';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { UpdateActionCustomPoint } from '@/connection/leaderboard';
import { actionLabelMap } from './types';
import { getInitialData } from '@/store/slices/leaderboard/initial';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { selectProjectEventId } from '@/store/slices/project/selectors';
import { getLeaderboardMainActions } from '@/store/slices/leaderboard/selectors';
import { updateActionPoints } from '@/store/slices/leaderboard';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import toastTrigger from '@ui/toast/toast-trigger';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { EToastTypes } from '@ui/toast/toast.types';

interface PointEditModalProps {
  open: boolean;
  closeFn: Dispatch<SetStateAction<boolean>>;
  initialActions: LeaderboardMultiplier[];
}

export const PointsEditModal = ({ open, closeFn, initialActions }: PointEditModalProps): JSX.Element => {
  const eventId = useAppSelector(selectProjectEventId);
  const activeChannel = useAppSelector(getActiveChannel);
  const loadedActions = useAppSelector(getLeaderboardMainActions);

  const [actions, setActions] = useState<LeaderboardMultiplier[]>(
    initialActions.length ? initialActions : getInitialData(eventId)
  );
  const [saving, setSaving] = useState(false);

  const dispatch = useAppDispatch();

  // if any of the actions have a multiplier less than 1, disbale save option
  const valid = useMemo(() => {
    let valid = true;
    actions.forEach((action) => {
      if (action.multiplier < 1 && action.is_on) {
        valid = false;
      }
    });
    return valid;
  }, [actions]);

  // a toggled on action will appear in the My Leaderboard section
  // a toggled off action will appear in the All Actions section
  const toggleAction = useCallback(
    (id: LeaderboardActions, isOn: boolean) => {
      const newActions = actions.map((action) => {
        if (action.action === id) {
          return { ...action, is_on: isOn };
        }
        return action;
      });
      setActions(newActions);
    },
    [actions]
  );

  useEffect(() => {
    if (!loadedActions.length) {
      setActions(getInitialData(eventId));
    } else {
      setActions(loadedActions);
    }
  }, [eventId, loadedActions]);

  const handleMultiplierChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, actionType: LeaderboardActions) => {
      e.stopPropagation();
      e.preventDefault();
      const { target } = e;
      const scoreSet = actions.map((action) => {
        if (action.action === actionType) {
          return { ...action, multiplier: Math.floor(Number(target.value)) };
        }
        return action;
      });
      setActions(scoreSet);
    },
    [actions]
  );

  const handleSubmit = async () => {
    setSaving(true);
    try {
      //Make sure the user toggle on the action and set the point for it before submitting. The default multiplier value is always 1. They have to set the point for the action or have to toggle it off.
      const isToggleOnWithoutValueSet = actions.some((action) => action.is_on && action.multiplier < 1);

      if (isToggleOnWithoutValueSet) {
        toastTrigger('Error submitting points', {
          description:
            'Some or all of the actions are toggled on but the point values are not set. Please update or toggle the unset action(s) off.',
          type: EToastTypes.error,
        });

        return;
      }

      if (activeChannel) {
        const finalActionsSent: LeaderboardMultiplier[] = [];
        for (const action of actions) {
          //the existence backend route only take one action per time.
          const result = await UpdateActionCustomPoint(action, activeChannel);
          if (result) {
            finalActionsSent.push(result);
          }
        }
        //update the updated array of actions.
        dispatch(updateActionPoints(finalActionsSent));
      }
      closeFn(false);
    } catch (error) {
      toastTrigger('Error updating points', {
        description: 'We were unable to update the action points. Please try again.',
        type: EToastTypes.error,
      });
      console.error(error);
    }

    setSaving(false);
  };

  const cancel = () => {
    if (!loadedActions.length) {
      setActions(getInitialData(eventId));
    } else {
      setActions(loadedActions);
    }
    closeFn(false);
  };

  // When we toggle on the action, display it on the leaderboard section.
  const renderLeaderboardAction = useCallback(
    (action: LeaderboardMultiplier): JSX.Element => (
      <Container
        background="var(--gray-900)"
        height="64px"
        className="flex ai-center jc-space-between padding-l-16 padding-r-16 radius-8"
        key={action.action}
      >
        <Text tag="span" className="S1" color="var(--gray-100)">
          {actionLabelMap[action.action]}
        </Text>
        <Container className="flex ai-center gap-10">
          <OptionalComponent display={action.is_on}>
            <InputWithOverlay
              status={action.multiplier < 1 ? 'error' : undefined}
              designSize="small"
              inputProps={{
                style: { width: '80px' },
                type: 'number',
                min: 1,
                className: 'score-point-input',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleMultiplierChange(e, action.action),
                value: String(action.multiplier),
                disabled: saving,
              }}
            />
          </OptionalComponent>
          <Switch value={action.action} onClick={toggleAction} on={action.is_on} key={action.action} />
        </Container>
      </Container>
    ),
    [handleMultiplierChange, toggleAction, saving]
  );

  const renderLists = useMemo(
    () => (
      <Container className="flex flex-column gap-10 padding-h-40 padding-v-16">
        {actions.map((action) => renderLeaderboardAction(action))}
      </Container>
    ),
    [actions, renderLeaderboardAction]
  );

  return (
    <Modal
      open={open}
      data-component="PointsEditModal"
      RenderHeaderContent={() => {
        return (
          <Container borderRadius="12px 12px 0 0" padding="40px 40px 8px 40px">
            <Text tag="h3" className="H3">
              Leaderboard
            </Text>

            <Text tag="h5" className="L1">
              Add or edit score settings
            </Text>
          </Container>
        );
      }}
      onRequestClose={cancel}
      contentWrapperClassName="edit-points-modal-leaderboard"
      customSize={{
        width: '640px',
        height: '425px',
        minHeight: '100px',
        maxHeight: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
      }}
      RenderFooterContent={() => (
        <Container className="flex fd-row jc-flex-end ai-center gap-16 padding-b-24 padding-r-40">
          <Button onClick={cancel} design="LocalBttnSec" className="L1" width="95px" height="48px">
            Cancel
          </Button>
          <Button
            backgroundColor={getThemeColorCSSVariable()}
            height="48px"
            minWidth="81px"
            loading={saving}
            disabled={saving || !valid}
            disabledOpacity={0.5}
            onClick={handleSubmit}
            className="flex ai-center jc-center"
          >
            <Text
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              className="L1"
            >
              Add
            </Text>
          </Button>
        </Container>
      )}
    >
      {renderLists}
    </Modal>
  );
};
